import React, { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ResponsiveDialog } from '@/components/ui/responsive-dialog';
import { Search, MessageCircle, ChevronLeft, ChevronRight, Mail, Filter, CheckCheck, Plus, X } from 'lucide-react';
import { cn, getAvatarColor, getFullPhoneE164 } from '@/lib/utils';
import { format } from 'date-fns';
import { safeFormat } from '@/lib/safeDate';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { ContactSearchCombobox, type ContactOption } from '@/components/common/ContactSearchCombobox';
import { useMobileUI } from '@/contexts/MobileUIContext';
import { useInboxConversations, type ConversationItem, type InboxFilter } from '../hooks/useInboxConversations';

export interface InboxLayoutProps {
  selectedContactId: string | null;
  onSelectContact: (contact: ConversationItem) => void;
  children: React.ReactNode;
}

const FILTER_LABELS: Record<InboxFilter, string> = {
  all: 'Todas',
  mine: 'Minhas',
  unanswered: 'Não Respondidas',
};

const DEFAULT_FILTER: InboxFilter = 'mine';

export function InboxLayout({ selectedContactId, onSelectContact, children }: InboxLayoutProps) {
  const inbox = useInboxConversations();
  const { setHideNavigation } = useMobileUI();

  const [showContactList, setShowContactList] = useState(true);
  const [mobileView, setMobileView] = useState<'list' | 'chat'>('list');
  const [filterOpen, setFilterOpen] = useState(false);
  const [newChatOpen, setNewChatOpen] = useState(false);
  const [newChatLoading, setNewChatLoading] = useState(false);

  // Mobile: switch to chat view when a contact is selected
  const handleSelect = (contact: ConversationItem) => {
    onSelectContact(contact);
    if (window.innerWidth < 768) {
      setMobileView('chat');
    }
  };

  // Mobile: go back to list
  const handleBackToList = () => {
    setMobileView('list');
  };

  // Open conversation with any contact (regardless of recent message history)
  const handleSelectNewContact = async (contactId: string, contact?: ContactOption) => {
    if (!contactId) return;
    setNewChatLoading(true);
    try {
      const { data, error } = await supabase
        .from('contacts')
        .select('id, first_name, last_name, primary_phone, primary_phone_country_code')
        .eq('id', contactId)
        .single();

      if (error || !data) {
        toast.error('Não foi possível carregar o contato');
        return;
      }

      const phone = getFullPhoneE164(data.primary_phone, data.primary_phone_country_code);
      if (!phone) {
        toast.error('Contato sem telefone cadastrado');
        return;
      }

      const fullName = [data.first_name, data.last_name].filter(Boolean).join(' ') || 'Sem nome';
      const item: ConversationItem = {
        id: data.id,
        full_name: fullName,
        phone,
        entityType: 'contact',
        lastMessage: null,
        lastMessageTime: null,
        lastMessageDirection: null,
        unreadCount: 0,
        hasMySentMessages: false,
        isAssignedToMe: false,
      };

      // Ensure conversation reappears if it was hidden
      inbox.unhideConversation('contact', data.id);
      onSelectContact(item);
      setNewChatOpen(false);
      if (window.innerWidth < 768) {
        setMobileView('chat');
      }
    } finally {
      setNewChatLoading(false);
    }
  };

  // Hide mobile navigation when in chat view
  useEffect(() => {
    if (window.innerWidth < 768) {
      setHideNavigation(mobileView === 'chat');
    }
    return () => setHideNavigation(false);
  }, [mobileView, setHideNavigation]);

  const isCustomFilter = inbox.filter !== DEFAULT_FILTER;

  return (
    <div className="flex h-full gap-2 overflow-hidden">
      {/* Conversations List */}
      <div
        className={cn(
          'border border-wa-divider flex flex-col bg-wa-sidebar transition-all duration-300 rounded-2xl shadow-sm overflow-hidden',
          showContactList ? 'md:w-[400px]' : 'md:w-0 md:overflow-hidden',
          'w-full',
          mobileView === 'chat' && 'hidden md:flex'
        )}
      >
        {/* Search + Filter */}
        <div className="p-2.5 bg-wa-sidebar border-b border-wa-divider">
          <div className="flex gap-2 items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-wa-text-muted h-4 w-4" />
              <Input
                placeholder="Pesquisar"
                value={inbox.searchQuery}
                onChange={(e) => inbox.setSearchQuery(e.target.value)}
                className="pl-9 w-full h-9 bg-wa-sidebar-active border-0 rounded-lg text-[14px] focus-visible:ring-1 focus-visible:ring-wa-green"
              />
            </div>

            <Button
              variant="ghost"
              size="sm"
              onClick={() => setNewChatOpen(true)}
              className="rounded-full h-9 w-9 p-0 flex-shrink-0 hover:bg-wa-sidebar-active text-wa-text-muted"
              title="Nova conversa"
            >
              <Plus className="h-4 w-4" />
            </Button>

            <Button
              variant="ghost"
              size="sm"
              onClick={() => setFilterOpen(true)}
              className={cn(
                'rounded-full h-9 w-9 p-0 flex-shrink-0',
                isCustomFilter
                  ? 'bg-wa-green text-white hover:bg-wa-green/90'
                  : 'hover:bg-wa-sidebar-active text-wa-text-muted'
              )}
              title={`Filtro: ${FILTER_LABELS[inbox.filter]}`}
            >
              <Filter className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <ResponsiveDialog open={newChatOpen} onOpenChange={setNewChatOpen} title="Nova conversa">
          <div className="grid gap-3 py-2">
            <Label className="text-xs text-muted-foreground">
              Busque qualquer contato do CRM para iniciar uma conversa
            </Label>
            <ContactSearchCombobox
              value=""
              onValueChange={handleSelectNewContact}
              placeholder={newChatLoading ? 'Abrindo conversa...' : 'Buscar contato...'}
            />
          </div>
        </ResponsiveDialog>

        <ResponsiveDialog open={filterOpen} onOpenChange={setFilterOpen} title="Filtros">
          <div className="grid gap-4 py-2">
            <div>
              <Label className="text-xs text-muted-foreground mb-2 block">Conversas</Label>
              <Select
                value={inbox.filter}
                onValueChange={(v) => inbox.setFilter(v as InboxFilter)}
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{FILTER_LABELS.all}</SelectItem>
                  <SelectItem value="mine">{FILTER_LABELS.mine}</SelectItem>
                  <SelectItem value="unanswered">{FILTER_LABELS.unanswered}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex items-center justify-between gap-2 pt-4 border-t mt-4">
            {isCustomFilter ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => inbox.setFilter(DEFAULT_FILTER)}
                className="text-destructive hover:text-destructive hover:bg-destructive/10"
              >
                Limpar filtros
              </Button>
            ) : (
              <span />
            )}
            <Button size="sm" onClick={() => setFilterOpen(false)}>
              Aplicar
            </Button>
          </div>
        </ResponsiveDialog>

        {/* Conversation items */}
        <div className="flex-1 overflow-y-auto bg-wa-sidebar">
          {inbox.conversations.map((contact) => (
            <div
              key={`${contact.entityType}-${contact.id}`}
              className={cn(
                'pl-3 pr-3 py-2.5 cursor-pointer hover:bg-wa-sidebar-active transition-colors group relative touch-action-manipulation',
                selectedContactId === contact.id && 'bg-wa-sidebar-active'
              )}
              onClick={() => handleSelect(contact)}
            >
              <div className="flex items-start gap-3">
                <Avatar className="h-12 w-12 flex-shrink-0">
                  <AvatarFallback className={`bg-gradient-to-br ${getAvatarColor(contact.full_name)} text-white text-base`}>
                    {contact.full_name?.charAt(0) || '?'}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0 border-b border-wa-divider pb-2 -mb-0.5">
                  <div className="flex items-center justify-between mb-0.5 gap-2">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <h3 className={cn(
                        'truncate text-[15px] text-wa-text',
                        contact.unreadCount > 0 ? 'font-semibold' : 'font-normal'
                      )}>
                        {contact.full_name || 'Sem nome'}
                      </h3>
                      {contact.entityType === 'lead' && (
                        <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4 bg-amber-100 text-amber-700 border-0">
                          Lead
                        </Badge>
                      )}
                    </div>
                    {contact.lastMessageTime && (
                      <span className={cn(
                        'text-[12px] flex-shrink-0',
                        contact.unreadCount > 0 ? 'text-wa-green font-medium' : 'text-wa-text-muted'
                      )}>
                        {safeFormat(new Date(contact.lastMessageTime), 'HH:mm')}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <div className={cn(
                      'flex items-center gap-1 min-w-0 flex-1 text-[13px]',
                      contact.unreadCount > 0 ? 'text-wa-text font-medium' : 'text-wa-text-muted'
                    )}>
                      {contact.lastMessageDirection === 'outbound' && (
                        <CheckCheck className="w-3.5 h-3.5 text-wa-text-muted shrink-0 [&>path]:stroke-[2.5]" />
                      )}
                      <span className="truncate">
                        {inbox.resolveBody(contact.lastMessage) || contact.phone}
                      </span>
                    </div>
                    {contact.unreadCount > 0 && (
                      <Badge className="bg-wa-green hover:bg-wa-green text-white border-0 rounded-full h-5 min-w-5 flex items-center justify-center px-1.5 text-[11px] font-semibold">
                        {contact.unreadCount}
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
              <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                {contact.unreadCount === 0 && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 bg-wa-sidebar hover:bg-wa-sidebar-active border border-wa-divider shadow-sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      inbox.markAsUnread(contact.id, contact.entityType);
                    }}
                    title="Marcar como não lida"
                  >
                    <Mail className="h-4 w-4 text-wa-text-muted" />
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 bg-wa-sidebar hover:bg-wa-sidebar-active border border-wa-divider shadow-sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    inbox.hideConversation(contact.entityType, contact.id);
                    toast.success('Conversa ocultada', {
                      duration: 5000,
                      action: {
                        label: 'Desfazer',
                        onClick: () => inbox.unhideConversation(contact.entityType, contact.id),
                      },
                    });
                  }}
                  title="Ocultar conversa"
                >
                  <X className="h-4 w-4 text-wa-text-muted" />
                </Button>
              </div>
            </div>
          ))}

          {inbox.conversations.length === 0 && !inbox.isLoading && (
            <div className="p-8 text-center text-wa-text-muted">
              <MessageCircle className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>Nenhuma conversa encontrada</p>
            </div>
          )}
        </div>
      </div>

      {/* Chat Area */}
      <div className={cn(
        'flex-1 flex flex-col relative',
        mobileView === 'list' && 'hidden md:flex'
      )}>
        {/* Toggle Button - desktop only */}
        <div className={cn(
          'absolute top-1/2 -translate-y-1/2 z-10 transition-all duration-300 hidden md:block',
          showContactList ? 'left-0' : '-left-4'
        )}>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setShowContactList(!showContactList)}
            className="bg-primary/90 hover:bg-primary text-primary-foreground border-none rounded-l-none rounded-r-full shadow-md h-10 w-6"
          >
            {showContactList ? <ChevronLeft className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </Button>
        </div>

        {/* Chat content or empty state */}
        {selectedContactId ? (
          children
        ) : (
          <div className="flex-1 flex items-center justify-center bg-card rounded-2xl shadow-sm border">
            <div className="text-center text-muted-foreground">
              <MessageCircle className="w-24 h-24 mx-auto mb-4 opacity-20" />
              <h3 className="text-xl font-semibold mb-2">Mensagens</h3>
              <p>Selecione uma conversa para começar</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Re-export for convenience
export { type ConversationItem, type InboxFilter } from '../hooks/useInboxConversations';
